import ARIAText from "../core/ARIAText";
import Strings from "../core/data/Strings";
import ResizableContainer from "../core/ResizableContainer";
import ResizableScene from "../core/ResizableScene";
import ScaleManager from "../core/ScaleManager";
import { SnowParticles } from "../game/SnowParticles";
import GameColors from "../GameColors";
import ARIA from "../utils/ARIA";
import Fonts from "../utils/Fonts";

export type StoryBeat = {
	text: string;
	header?: string;
	image: string;
};

export default class StoryMoment extends ResizableContainer {
	scene: ResizableScene;

	private _onResize: () => void;

	private storyText: ARIAText;

	private storyBeats: StoryBeat[];
	private storyIndex = 0;

	image: Phaser.GameObjects.Image;

	private _onComplete: () => void;
	private _onCompleteContext: CallbackContext;

	constructor(
		scene: ResizableScene,
		storyBeats: StoryBeat[],
		completeScreen = false,
		onComplete?: () => void,
		onCompleteContext?: CallbackContext
	) {
		super(scene, 0, 0);

		this._onComplete = onComplete;
		this._onCompleteContext = onCompleteContext;

		this.storyBeats = storyBeats;
		this.storyIndex = 0;

		const overlay = scene.add.image(0, 0, "popupOverlay");
		overlay.setInteractive();
		this.add(overlay);

		if (completeScreen) {
			this.setSize(scene.viewport.width, scene.viewport.height);
			SnowParticles.create(scene, this);
		}

		const popupContainer = scene.add.container();
		this.add(popupContainer);

		const backer = this.scene.add.image(0, 0, "storyBacker");
		popupContainer.add(backer);

		const image = (this.image = scene.add.image(0, 0, storyBeats[0].image));
		popupContainer.add(image);

		const textGroup = this.scene.add.container();
		popupContainer.add(textGroup);

		const bubble = scene.add.image(0, 0, "storyTextBacker");
		textGroup.add(bubble);
		textGroup.setSize(bubble.width, bubble.height);

		const storyText = (this.storyText = this.scene.add.text(0, 0, Strings.get(storyBeats[0].text), {
			color: " #686ab5",
			fontSize: "30px",
			fontFamily: Fonts.MontserratBold,
			align: "center",
			wordWrap: { width: bubble.width - 150 },
		}));
		storyText.setLineSpacing(20);
		textGroup.add(storyText);

		const nextButton = scene.add.button(0, 0, "nextBtn", 0, this.nextStory, this, true);
		popupContainer.add(nextButton);

		const skipButton = scene.add.button(0, 0, "skipBtn", 0, this.skipStory, this, true);
		popupContainer.add(skipButton);
		if (storyBeats.length < 2) skipButton.setVisible(false);

		// setup complete screen assets
		const headerBg = scene.add.image(0, 0, "popupHeader");
		const headerStr = storyBeats[0].header ? Strings.get(storyBeats[0].header) : "";
		const headerText = this.scene.add.text(0, 0, headerStr, {
			color: GameColors.WHITE,
			fontSize: "70px",
			fontFamily: Fonts.MontserratBold,
			align: "center",
			wordWrap: { width: bubble.width - 150 },
		});

		const leftDeco = scene.add.image(0, 0, "leftDeco");
		const rightDeco = scene.add.image(0, 0, "rightDeco");
		popupContainer.add([headerBg, headerText, leftDeco, rightDeco]);

		if (!completeScreen) {
			headerBg.setVisible(false);
			headerText.setVisible(false);
			leftDeco.setVisible(false);
			rightDeco.setVisible(false);
		}

		this._onResize = (): void => {
			ScaleManager.ScaleBackgroundFromZone(overlay, scene.viewport);
			scene.alignIn.Center(overlay, scene.viewport);
			scene.alignIn.Center(popupContainer, scene.viewport);
			scene.alignIn.Center(image, backer, 0, -310);

			this.scene.alignIn.Center(textGroup, backer, 0, 30);
			this.scene.alignIn.Center(bubble, textGroup);
			this.scene.alignIn.Center(storyText, bubble);

			scene.alignIn.TopCenter(headerBg, backer, 0, headerBg.displayHeight / 2);
			scene.alignIn.Center(headerText, headerBg);
			scene.alignIn.BottomLeft(leftDeco, backer, leftDeco.displayWidth / 2, 50);
			scene.alignIn.BottomRight(rightDeco, backer, rightDeco.displayWidth / 2, 50);

			scene.alignIn.BottomCenter(nextButton, backer, 0, 50);
			scene.alignTo.LeftCenter(skipButton, nextButton, 15, 0);
		};
		this.resize();

		const ariaLayer = "story-moment";
		storyText.setARIA(ariaLayer, true);
		nextButton.setARIA("Next", ariaLayer);
		skipButton.setARIA("Skip", ariaLayer);
		ARIA.showLayer(ariaLayer, true);
	}

	private nextStory(): void {
		this.storyIndex++;
		if (this.storyIndex >= this.storyBeats.length) this.skipStory();
		else {
			this.storyText.setText(Strings.get(this.storyBeats[this.storyIndex].text));
			this.image.setTexture(this.storyBeats[this.storyIndex].image);
			this.resize();
			this.storyText.focusARIA();
		}
	}

	private skipStory(): void {
		if (this._onComplete) {
			if (this._onCompleteContext) this._onComplete.call(this._onCompleteContext);
			else this._onComplete();
		}
		ARIA.showLayer(undefined, true);
		this.destroy();
	}

	resize(): void {
		super.resize();
		if (this._onResize) this._onResize();
	}
}
